import type { FormEvent } from 'react';

/** Stops the browser submit and returns the form's text fields. */
export function readForm(event: FormEvent<HTMLFormElement>): Record<string, string> {
  event.preventDefault();
  const values: Record<string, string> = {};
  for (const [name, value] of new FormData(event.currentTarget)) {
    if (typeof value === 'string') values[name] = value;
  }
  return values;
}

/**
 * Quick check before the API answers: a public http(s) address whose host has a dot. The server still
 * decides (it refuses private hosts, reserved TLDs, credentials…); this only catches obvious mistakes
 * such as «hola» so the field can say what is wrong without waiting.
 */
export function looksLikeSiteUrl(raw: string): boolean {
  const value = raw.trim();
  if (!value) return false;
  const candidate = /^[a-z][a-z\d+.-]*:\/\//i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(candidate);
    const host = url.hostname.toLowerCase();
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return false;
    if (url.username || url.password) return false;
    if (!host.includes('.') || host.startsWith('[') || /^[\d.]+$/.test(host)) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Where to go after signing in: only paths on this site. "//host" and "/\host" are rejected because
 * browsers treat them as another origin.
 */
export function safeRedirect(value: unknown): string | undefined {
  return typeof value === 'string' && /^\/(?![/\\])/.test(value) ? value : undefined;
}

/**
 * The language of the sign-in page. The one the visitor chose on the public website arrives as ?lang=
 * in the link; without it the browser's preference decides, and the fallback is used when neither is
 * a supported language.
 */
export function pickLocale<T extends string>(
  search: string,
  browserLanguages: readonly string[],
  supported: readonly T[],
  fallback: T,
): T {
  const asked = new URLSearchParams(search).get('lang')?.toLowerCase();
  const candidates = [asked, ...browserLanguages.map((tag) => tag.slice(0, 2).toLowerCase())];
  return candidates.find((code): code is T => supported.includes(code as T)) ?? fallback;
}

/**
 * The name of an account that only gave an email: the part before the @ ("ana.garcia@tienda.es" →
 * "ana.garcia"), which Better Auth needs and the dashboard shows in the greeting.
 */
export function nameFromEmail(email: string): string {
  return (email.trim().split('@')[0] ?? '').slice(0, 60) || email.trim().slice(0, 60);
}

const WEBSITE = 'https://sitenorma.com';
const PUBLIC_PAGES = {
  es: {
    home: '/',
    privacy: '/privacidad/',
    terms: '/condiciones/',
    methodology: '/metodologia/',
    pricing: '/precios/',
    pages: '/precios/#pagina-analizada',
    contact: '/contacto/',
  },
  en: {
    home: '/en/',
    privacy: '/en/privacy/',
    terms: '/en/terms/',
    methodology: '/en/methodology/',
    pricing: '/en/pricing/',
    pages: '/en/pricing/#pagina-analizada',
    contact: '/en/contact/',
  },
} as const;

/**
 * A page of the public website in the user's language. It exists in Spanish and English; the other
 * languages of the app link to the English one.
 */
export function publicPage(language: string, page: keyof (typeof PUBLIC_PAGES)['es']): string {
  return `${WEBSITE}${PUBLIC_PAGES[language === 'es' ? 'es' : 'en'][page]}`;
}

/** A URL-friendly organisation slug with a random suffix, e.g. "Tienda Ñandú" → "tienda-nandu-k3f9x2". */
export function slugify(name: string, random = () => Math.random().toString(36).slice(2, 8)): string {
  const base = name
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
    .replace(/-+$/, '');
  return base ? `${base}-${random()}` : random();
}

/** The part of an address that tells pages apart: "/" for the home page, path and query otherwise. */
export function pathOf(url: string): string {
  try {
    const parsed = new URL(url);
    return `${decodeURI(parsed.pathname)}${parsed.search}` || '/';
  } catch {
    return url;
  }
}

/** "812 KB", "1,4 MB": the weight of a page. */
export function formatBytes(bytes: number, locale: string): string {
  const format = (value: number, unit: string, digits: number) =>
    `${new Intl.NumberFormat(locale, { maximumFractionDigits: digits }).format(value)} ${unit}`;
  if (bytes < 1000) return format(bytes, 'B', 0);
  if (bytes < 1_000_000) return format(bytes / 1000, 'KB', 0);
  return format(bytes / 1_000_000, 'MB', 1);
}

/** "0,9 s", "12 s": a duration in seconds, with one decimal under ten. */
export function formatSeconds(ms: number, locale: string): string {
  const seconds = ms / 1000;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: seconds < 10 ? 1 : 0 }).format(seconds)} s`;
}

/** "01:42": elapsed time on a clock. */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(total / 60);
  return `${String(minutes).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * Where a confirmation link sends someone whose email it could not confirm: back to the page they asked for
 * with `?error=…` added (the service's own redirect). It splits that into the page they really wanted and
 * whether the link failed, so signing in goes to the page and the failure is told once.
 */
export function splitLinkFailure(redirect: string | undefined): {
  target: string | undefined;
  linkFailed: boolean;
} {
  if (!redirect) return { target: undefined, linkFailed: false };
  try {
    const url = new URL(redirect, 'https://panel.invalid');
    const error = url.searchParams.get('error');
    if (error === null) return { target: redirect, linkFailed: false };
    url.searchParams.delete('error');
    const target = `${url.pathname}${url.search}${url.hash}`;
    return { target: target === '/' ? undefined : target, linkFailed: /token/i.test(error) };
  } catch {
    return { target: redirect, linkFailed: false };
  }
}

/**
 * Where the link of the verification email lands: the page that tells the person what happened. The page they
 * wanted before (an invitation, say) travels as `next` so that «Iniciar sesión» goes back to it.
 */
export function verificationCallback(next?: string): string {
  return `/verify-email?estado=verificada${next ? `&next=${encodeURIComponent(next)}` : ''}`;
}

export type GoogleProblem = 'cancelled' | 'noAccount' | 'notLinked' | 'notVerified' | 'failed';

/**
 * What came back from Google, from the `?error=` the sign-in service adds to the page it returns to. No
 * code means the person did not come back from a failed attempt.
 */
export function googleProblem(code: unknown): GoogleProblem | null {
  if (typeof code !== 'string' || !code) return null;
  switch (code) {
    case 'access_denied':
      return 'cancelled';
    case 'signup_disabled':
      return 'noAccount';
    case 'account_not_linked':
    case 'unable_to_link_account':
      return 'notLinked';
    case 'email_not_verified':
      return 'notVerified';
    default:
      return 'failed';
  }
}
